import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, InputNumber, Card, Divider, message, Space, Tag, Switch, DatePicker, Row, Col, Statistic, Collapse, Grid, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, CheckCircleOutlined, EditOutlined, FilterOutlined, ClearOutlined, EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { satislarAPI, urunlerAPI, gelirTipleriAPI, musterilerAPI, listeFiyatlariAPI, kasalarAPI, gununMenuleriAPI, satisTakvimiAPI, takvimEkstraMenuleriAPI, musteriAnlasmaFiyatiTarihceAPI, satisVadeleriAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara, paraInputProps } from '../utils/format';
import { negatifKayitOnayi } from '../utils/onay';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';

// fatura_durumu id → tag rengi (DB'de tutulmuyor, sadece UI dekoru)
const FATURA_DURUMU_RENK = { 1: 'green', 2: 'blue', 3: 'orange', 4: 'default' };

// fatura_durumlari tablosundaki sabit ID'ler
const FATURA_DURUMU = {
  FATURA_KESILDI: 1,
  FIS_KESILDI: 2,
  BEKLEMEDE: 3,
  FATURASIZ: 4,
};

// Sabit gelir tipi id'si
const SABIT_GELIR_TIPI_ID = 2;

function Satislar() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { kdvOranlari: kdvOranlariRaw, faturaDurumlari, faturaDurumuOptions } = useLookups();
  const kdvOranlari = kdvOranlariRaw.map(k => ({ value: Number(k.oran), label: '%' + Number(k.oran) }));
  const kdvIdToOran = Object.fromEntries(kdvOranlariRaw.map(k => [k.id, Number(k.oran)]));

  const [data, setData] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [gelirTipleri, setGelirTipleri] = useState([]);
  const [musteriler, setMusteriler] = useState([]);
  const [listeFiyatlari, setListeFiyatlari] = useState([]);
  const [kasalar, setKasalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Filtre state'leri
  const [filtreCariIds, setFiltreCariIds] = useState([]);
  const [filtreGelirTipiIds, setFiltreGelirTipiIds] = useState([]);
  const [filtreFaturaDurumlari, setFiltreFaturaDurumlari] = useState([]);
  const [filtreOdenmeDurumlari, setFiltreOdenmeDurumlari] = useState([]);
  const [filtreTutarOp, setFiltreTutarOp] = useState(null);
  const [filtreTutarDeger, setFiltreTutarDeger] = useState(null);
  const [filtreTarihBaslangic, setFiltreTarihBaslangic] = useState(null);
  const [filtreTarihBitis, setFiltreTarihBitis] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);

  // Form state
  const [seciliGelirTipi, setSeciliGelirTipi] = useState(null);
  const [seciliMusteri, setSeciliMusteri] = useState(null);
  const [vadeModalOpen, setVadeModalOpen] = useState(false);
  const [vadeData, setVadeData] = useState({ kayit: null, vadeler: [] });
  const [menuAdet, setMenuAdet] = useState(1);
  const [ilaveler, setIlaveler] = useState([]);
  const [faturaDurumu, setFaturaDurumu] = useState(4);
  const [hemenOdendi, setHemenOdendi] = useState(false);
  const [seciliKasa, setSeciliKasa] = useState(null);
  const [aciklama, setAciklama] = useState('');
  const [menuKdvOrani, setMenuKdvOrani] = useState(10);
  const [satisTarih, setSatisTarih] = useState(dayjs());
  // Sabit gelir tipi için anlaşma birim fiyatı: yeni kayıtta tarihçeden / edit'te kalem.birim_fiyat'tan yüklenir, kullanıcı override edebilir.
  const [sabitBirimFiyat, setSabitBirimFiyat] = useState(0);
  // Anlaşma fiyat tipi: 'sabit' | 'tutar_indirim' | 'yuzde_indirim'
  const [sabitFiyatTipi, setSabitFiyatTipi] = useState('sabit');
  const [sabitIndirimDegeri, setSabitIndirimDegeri] = useState(0);
  const [gununMenusu, setGununMenusu] = useState(null);
  const [gununTakvimUrunleri, setGununTakvimUrunleri] = useState([]);
  const [gununEkstraMenuleri, setGununEkstraMenuleri] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [satisRes, urunRes, gelirRes, musteriRes, listeRes, kasaRes] = await Promise.all([
        satislarAPI.getAll(),
        urunlerAPI.getAll(),
        gelirTipleriAPI.getAll(),
        musterilerAPI.getAll(),
        listeFiyatlariAPI.getAll(),
        kasalarAPI.getAll(),
      ]);
      setData(satisRes.data);
      setUrunler(urunRes.data);
      setGelirTipleri(gelirRes.data);
      setMusteriler(musteriRes.data.filter(m => m.aktif && m.cari_aktif));
      setListeFiyatlari(listeRes.data);
      setKasalar(kasaRes.data.filter(k => k.aktif));
    } catch (err) {
      message.error('Veriler yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const duzenleId = searchParams.get('duzenle');
    if (duzenleId && data.length > 0 && !modalOpen) {
      handleDuzenle(Number(duzenleId));
      navigate('/satislar', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  // Sabit + müşteri seçilince tarihçeden o tarih için aktif anlaşma fiyatını yükle.
  // Anlaşma fiyatı her zaman tarihçe = tek kaynak; UI'den manuel override edilemez.
  const sifirlaAnlasma = () => { setSabitBirimFiyat(0); setSabitFiyatTipi('sabit'); setSabitIndirimDegeri(0); };
  useEffect(() => {
    if (seciliGelirTipi !== SABIT_GELIR_TIPI_ID) { sifirlaAnlasma(); return; }
    if (!seciliMusteri?.id) { sifirlaAnlasma(); return; }
    const tarih = (satisTarih || dayjs()).format('YYYY-MM-DD');
    musteriAnlasmaFiyatiTarihceAPI.getActiveByDate(seciliMusteri.id, tarih)
      .then(res => {
        const tipi = res.data?.fiyat_tipi || 'sabit';
        const deger = Number(res.data?.fiyat) || 0;
        setSabitFiyatTipi(tipi);
        // 'sabit' → KDV hariç sabit birim fiyat; indirim tipleri → indirim değeri
        setSabitBirimFiyat(tipi === 'sabit' ? deger : 0);
        setSabitIndirimDegeri(tipi === 'sabit' ? 0 : deger);
      })
      .catch(() => sifirlaAnlasma());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seciliGelirTipi, seciliMusteri, satisTarih]);

  const isSabit = seciliGelirTipi === SABIT_GELIR_TIPI_ID;

  // Günün menüsü bir set fiyatı: ürünlerin liste fiyatı × adet toplamı − menü indirimi (KDV dahil)
  const getMenuSetFiyati = () => {
    if (!gununMenusu || !gununMenusu.kalemler?.length) return 0;
    const urunToplam = gununMenusu.kalemler.reduce((t, k) => t + getUrunListeFiyati(k.urun_id) * (k.adet || 1), 0);
    const indirimRec = listeFiyatlari.find(l => l.tur === 'indirim' && l.aktif);
    const indirim = indirimRec ? Number(indirimRec.fiyat) : 0;
    return Math.max(0, urunToplam - indirim);
  };

  const getUrunListeFiyati = (itemId) => {
    const urun = urunler.find(u => u.id === itemId);
    if (urun && urun.urun_tipi_id) {
      const lf = listeFiyatlari.find(l => l.tur === 'urun' && l.urun_tipi_id === urun.urun_tipi_id && l.aktif);
      if (lf) return Number(lf.fiyat);
    }
    if (urun && urun.satis_fiyati) return Number(urun.satis_fiyati);
    return 0;
  };

  // Ekstra menü fiyatı: ürünlerin liste fiyatı × adet toplamı − menü indirimi (KDV dahil)
  const getEkstraMenuFiyati = (ekstraMenuId) => {
    const em = gununEkstraMenuleri.find(m => m.id === ekstraMenuId);
    if (!em) return 0;
    const urunToplam = em.kalemler.reduce((t, k) => t + getUrunListeFiyati(k.urun_id) * (k.adet || 1), 0);
    const indirimRec = listeFiyatlari.find(l => l.tur === 'indirim' && l.aktif);
    const indirim = indirimRec ? Number(indirimRec.fiyat) : 0;
    return Math.max(0, urunToplam - indirim);
  };

  const isEkstraMenuId = (v) => typeof v === 'string' && v.startsWith('ekstra_');
  const ekstraMenuIdFromKey = (v) => Number(String(v).replace('ekstra_', ''));

  // Liste fiyatı bul (KDV dahil)
  const getListeFiyati = (itemId) => {
    if (itemId === 'menu') return getMenuSetFiyati();
    if (isEkstraMenuId(itemId)) return getEkstraMenuFiyati(ekstraMenuIdFromKey(itemId));
    return getUrunListeFiyati(itemId);
  };

  const resetForm = () => {
    setSeciliGelirTipi(null);
    setSeciliMusteri(null);
    setMenuAdet(1);
    setIlaveler([]);
    setFaturaDurumu(FATURA_DURUMU.FATURASIZ);
    setHemenOdendi(false);
    setSeciliKasa(null);
    setAciklama('');
    setMenuKdvOrani(10);
    setGununMenusu(null);
    setGununTakvimUrunleri([]);
    setGununEkstraMenuleri([]);
    setSatisTarih(dayjs());
    sifirlaAnlasma();
  };

  const fetchGununMenusu = async (tarih) => {
    try {
      const [menuRes, takvimRes, ekstraRes] = await Promise.all([
        gununMenuleriAPI.getByDate(tarih),
        satisTakvimiAPI.getByDate(tarih),
        takvimEkstraMenuleriAPI.getByDate(tarih),
      ]);
      setGununMenusu(menuRes.data || null);
      setGununTakvimUrunleri(takvimRes.data || []);
      setGununEkstraMenuleri(ekstraRes.data || []);
    } catch {
      setGununMenusu(null);
      setGununTakvimUrunleri([]);
      setGununEkstraMenuleri([]);
    }
  };

  // Modal açılırken fiyat ve ürün lookup'larını tazele (başka ekranda değişmiş olabilir)
  const refreshLookups = async () => {
    try {
      const [urunRes, listeRes] = await Promise.all([urunlerAPI.getAll(), listeFiyatlariAPI.getAll()]);
      setUrunler(urunRes.data);
      setListeFiyatlari(listeRes.data);
    } catch (err) {
      console.error('Lookup tazeleme başarısız', err);
    }
  };

  const handleModalAc = () => {
    resetForm();
    setEditId(null);
    refreshLookups();
    fetchGununMenusu(dayjs().format('YYYY-MM-DD'));
    setModalOpen(true);
  };

  const handleTarihChange = (yeniTarih) => {
    const t = yeniTarih || dayjs();
    setSatisTarih(t);
    fetchGununMenusu(t.format('YYYY-MM-DD'));
  };

  const handleSil = async (id) => {
    try {
      await satislarAPI.remove(id);
      message.success('Satış silindi');
      setSelectedRowKeys(prev => prev.filter(k => k !== id));
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleTopluSil = async () => {
    if (selectedRowKeys.length === 0) return;
    const hide = message.loading(`${selectedRowKeys.length} kayıt siliniyor...`, 0);
    try {
      const sonuclar = await Promise.allSettled(
        selectedRowKeys.map(id => satislarAPI.remove(id))
      );
      hide();
      const basarili = sonuclar.filter(r => r.status === 'fulfilled').length;
      const basarisiz = sonuclar.length - basarili;
      if (basarili > 0) message.success(`${basarili} kayıt silindi`);
      if (basarisiz > 0) {
        const ilkHata = sonuclar.find(r => r.status === 'rejected');
        message.error(`${basarisiz} kayıt silinemedi: ${ilkHata?.reason?.response?.data?.hata || ilkHata?.reason?.message || ''}`);
      }
      setSelectedRowKeys([]);
      fetchData();
    } catch (err) {
      hide();
      message.error('Silme işlemi başarısız: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleDuzenle = async (id) => {
    try {
      await refreshLookups();
      const res = await satislarAPI.getById(id);
      const satis = res.data;
      setEditId(id);
      fetchGununMenusu(dayjs(satis.tarih).format('YYYY-MM-DD'));

      // Gelir tipi
      setSeciliGelirTipi(satis.gelir_tipi_id);
      const sabit = satis.gelir_tipi_id === SABIT_GELIR_TIPI_ID;

      // Müşteri
      if (satis.cari_id) {
        const musteri = musteriler.find(m => m.cari_id === satis.cari_id);
        setSeciliMusteri(musteri || null);
      } else {
        setSeciliMusteri(null);
      }

      // Fatura durumu, ödeme, açıklama
      // hemen_odendi state'i satis.kasa_id'den türet — odendi türetilmiş bir alan
      // (vade kapandığı her durumda 1) ve kayıt anındaki "Hemen tahsil edildi" tercihini
      // bize söylemez; satislar.kasa_id ise sadece kayıt anında "Hemen tahsil edildi"
      // işaretliyse dolu olur.
      setFaturaDurumu(satis.fatura_durumu_id || 4);
      setHemenOdendi(!!satis.kasa_id);
      setSeciliKasa(satis.kasa_id || null);
      setAciklama(satis.aciklama || '');

      // Kalemleri yükle
      const kalemler = satis.kalemler || [];

      // Sabit: ilk menu kalemi anlasma menüsü (menuAdet), diğer menu + tüm urun kalemleri ilaveler.
      // Sabit dışı: tüm urun ve menu kalemleri ilaveler.
      let ilaveKalemler;
      if (sabit) {
        const menuKalemler = kalemler.filter(k => k.tur === 'menu');
        setMenuAdet(menuKalemler[0]?.adet || 1);
        // Kayıttan KDV oranını da geri yükle (default 10'a dönmesin)
        const ilkMenuKalem = menuKalemler[0];
        if (ilkMenuKalem && ilkMenuKalem.birim_fiyat > 0) {
          const kdvBirim = Number(ilkMenuKalem.kdv_tutar || 0) / Number(ilkMenuKalem.adet || 1);
          const oran = Math.round((kdvBirim / Number(ilkMenuKalem.birim_fiyat)) * 100);
          setMenuKdvOrani([0, 1, 10, 20].includes(oran) ? oran : 10);
        }
        // Anlaşma fiyatı useEffect üzerinden tarihçeden yüklenir; kalemden okuma yapma.
        ilaveKalemler = [
          ...kalemler.filter(k => k.tur === 'urun'),
          ...menuKalemler.slice(1),
        ];
      } else {
        setMenuAdet(1);
        sifirlaAnlasma();
        ilaveKalemler = kalemler.filter(k => k.tur === 'urun' || k.tur === 'menu');
      }
      setSatisTarih(dayjs(satis.tarih));
      setIlaveler(ilaveKalemler.map(k => {
        const kdvDahil = k.kdv_dahil_tutar ? Number(k.kdv_dahil_tutar) / (k.adet || 1) : Number(((k.birim_fiyat || 0) + (k.kdv_tutar || 0) / (k.adet || 1)).toFixed(2));
        const kdvHaric = Number(k.birim_fiyat) || 0;
        return {
          key: Date.now() + Math.random(),
          urun_id: k.tur === 'menu' ? 'menu' : k.urun_id,
          adet: k.adet || 1,
          fiyatKdvDahil: kdvDahil,
          fiyatKdvHaric: kdvHaric,
          kdv_orani: k.birim_fiyat > 0 && k.kdv_tutar > 0 ? Math.round((k.kdv_tutar / (k.adet || 1)) / k.birim_fiyat * 100) : 10,
        };
      }));

      setModalOpen(true);
    } catch (err) {
      message.error('Satış yüklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleGelirTipiSec = (id) => {
    setSeciliGelirTipi(id);
    setSeciliMusteri(null);
    setMenuAdet(1);
    setIlaveler([]);
    if (id === SABIT_GELIR_TIPI_ID) {
      setHemenOdendi(false);
      setSeciliKasa(null);
    } else {
      setHemenOdendi(true);
      setFaturaDurumu(FATURA_DURUMU.FATURASIZ);
      // Gelir tipi adıyla eşleşen kasayı otomatik seç
      const gelirTipi = gelirTipleri.find(g => g.id === id);
      if (gelirTipi) {
        const eslesenKasa = kasalar.find(k => k.ad.toLowerCase() === gelirTipi.ad.toLowerCase());
        if (eslesenKasa) {
          setSeciliKasa(eslesenKasa.id);
          if (eslesenKasa.ad.toLowerCase().replace(/\s/g, '').startsWith('kuveyttürk')) {
            setFaturaDurumu(FATURA_DURUMU.FIS_KESILDI);
          }
        } else {
          setSeciliKasa(null);
        }
      }
    }
  };

  const handleMusteriSec = (cariId) => {
    const musteri = musteriler.find(m => m.cari_id === cariId) || null;
    setSeciliMusteri(musteri);
    // Fatura durumunu otomatik belirle
    if (musteri) {
      setFaturaDurumu(musteri.faturali ? FATURA_DURUMU.BEKLEMEDE : FATURA_DURUMU.FATURASIZ);
      setMenuKdvOrani(musteri.faturali ? 10 : 0);
    }
  };

  // İlave ürün ekleme
  const ilaveEkle = () => {
    setIlaveler([...ilaveler, { key: Date.now(), urun_id: null, adet: 1, fiyatKdvDahil: 0, fiyatKdvHaric: 0, kdv_orani: 10 }]);
  };

  const ilaveGuncelle = (key, field, value) => {
    setIlaveler(prev => prev.map(item => {
      if (item.key !== key) return item;
      const updated = { ...item, [field]: value };
      if (field === 'urun_id') {
        updated.fiyatKdvDahil = getListeFiyati(value);
        if (value === 'menu' || isEkstraMenuId(value)) {
          updated.kdv_orani = 10;
        } else {
          const urun = urunler.find(u => u.id === value);
          if (urun && urun.urun_tipi_id) {
            const lf = listeFiyatlari.find(l => l.tur === 'urun' && l.urun_tipi_id === urun.urun_tipi_id && l.aktif);
            if (lf && lf.kdv_id) updated.kdv_orani = kdvIdToOran[lf.kdv_id] ?? 10;
          }
        }
      }
      // KDV oranı veya iki fiyat alanından biri değişince diğerini otomatik ayarla
      const oran = Number(updated.kdv_orani) || 0;
      if (field === 'fiyatKdvDahil') {
        const dahil = Number(value) || 0;
        updated.fiyatKdvHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
      } else if (field === 'fiyatKdvHaric') {
        const haric = Number(value) || 0;
        updated.fiyatKdvDahil = oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric;
      } else if (field === 'kdv_orani' || field === 'urun_id') {
        // Dahil sabit, hariç türet
        const dahil = Number(updated.fiyatKdvDahil) || 0;
        updated.fiyatKdvHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
      }
      return updated;
    }));
  };

  const ilaveSil = (key) => {
    setIlaveler(prev => prev.filter(i => i.key !== key));
  };

  // Hesaplamalar
  // Sabit fiyat tipine göre menü birim fiyatı (KDV hariç):
  //  'sabit'         → tarihçedeki KDV hariç sabit birim fiyat
  //  'tutar_indirim' → günün menüsü (KDV dahil) − indirim TL → KDV ayrıştır
  //  'yuzde_indirim' → günün menüsü (KDV dahil) × (1 − %)   → KDV ayrıştır
  // İndirim tabanı = getMenuSetFiyati() (global menü indirimi düşülmüş günün menüsü satış fiyatı).
  const sabitMenuTabanDahil = (isSabit && sabitFiyatTipi !== 'sabit') ? getMenuSetFiyati() : 0;
  const sabitMenuIndirimliDahil = (() => {
    if (sabitFiyatTipi === 'tutar_indirim') return Math.max(0, sabitMenuTabanDahil - (Number(sabitIndirimDegeri) || 0));
    if (sabitFiyatTipi === 'yuzde_indirim') return Math.max(0, sabitMenuTabanDahil * (1 - (Number(sabitIndirimDegeri) || 0) / 100));
    return 0;
  })();
  const anlasmaBirimFiyat = (() => {
    if (sabitFiyatTipi === 'sabit') return Number(sabitBirimFiyat) || 0;
    const oran = Number(menuKdvOrani) || 0;
    const haric = oran > 0 ? sabitMenuIndirimliDahil / (1 + oran / 100) : sabitMenuIndirimliDahil;
    return Number(haric.toFixed(2));
  })();
  const menuToplamKdvHaric = isSabit ? anlasmaBirimFiyat * menuAdet : 0;
  const menuKdv = Number((menuToplamKdvHaric * menuKdvOrani / 100).toFixed(2));

  // Her ilave kalemi kendi KDV oranına göre hesapla
  const ilaveToplamKdvDahil = ilaveler.reduce((t, i) => t + (i.fiyatKdvDahil || 0) * (i.adet || 0), 0);
  const ilaveToplamKdvHaric = ilaveler.reduce((t, i) => {
    const dahil = (i.fiyatKdvDahil || 0) * (i.adet || 0);
    const oran = i.kdv_orani || 0;
    return t + (oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil);
  }, 0);
  const ilaveKdv = Number((ilaveToplamKdvDahil - ilaveToplamKdvHaric).toFixed(2));

  const genelKdvHaric = menuToplamKdvHaric + ilaveToplamKdvHaric;
  const genelKdv = menuKdv + ilaveKdv;
  const genelToplam = menuToplamKdvHaric + menuKdv + ilaveToplamKdvDahil;

  const handleKaydet = async () => {
    if (!seciliGelirTipi) {
      message.error('Gelir tipi seçin');
      return;
    }

    if (isSabit && !seciliMusteri) {
      message.error('Sabit gelir tipi için müşteri seçin');
      return;
    }

    // İndirim tipli müşteride taban = o günün menüsü; menü tanımlı değilse indirim hesaplanamaz.
    if (isSabit && sabitFiyatTipi !== 'sabit' && (!gununMenusu || !gununMenusu.kalemler?.length)) {
      message.error('İndirimli müşteri için önce bu tarihe ait günün menüsünü tanımlayın.');
      return;
    }

    if (!isSabit && ilaveler.length === 0) {
      message.error('En az bir ürün veya menü ekleyin');
      return;
    }

    const kalemler = [];

    // Sabit: menü kalemi
    if (isSabit && menuAdet > 0 && anlasmaBirimFiyat > 0) {
      const menuKdvTutar = Number((anlasmaBirimFiyat * menuAdet * menuKdvOrani / 100).toFixed(2));
      kalemler.push({
        tur: 'menu',
        urun_id: null,
        adet: menuAdet,
        birim_fiyat: anlasmaBirimFiyat,
        kdv_tutar: menuKdvTutar,
        kdv_dahil_tutar: Number((anlasmaBirimFiyat * menuAdet + menuKdvTutar).toFixed(2)),
        maliyet: 0,
      });
    }

    // İlave kalemler (liste fiyatı KDV dahil → ayrıştır per-item KDV oranına göre)
    ilaveler.forEach(i => {
      if (!i.urun_id) return;
      const isMenu = i.urun_id === 'menu' || isEkstraMenuId(i.urun_id);
      const kdvDahilBirim = i.fiyatKdvDahil || 0;
      const oran = i.kdv_orani || 0;
      const kdvHaricBirim = oran > 0 ? Number((kdvDahilBirim / (1 + oran / 100)).toFixed(2)) : kdvDahilBirim;
      const adet = i.adet || 1;
      const kdvTutar = Number(((kdvDahilBirim - kdvHaricBirim) * adet).toFixed(2));

      kalemler.push({
        tur: isMenu ? 'menu' : 'urun',
        urun_id: isMenu ? null : i.urun_id,
        adet,
        birim_fiyat: kdvHaricBirim,
        kdv_tutar: kdvTutar,
        kdv_dahil_tutar: Number((kdvDahilBirim * adet).toFixed(2)),
        maliyet: 0,
      });
    });

    if (kalemler.length === 0) {
      message.error('En az bir kalem gerekli');
      return;
    }

    if (genelToplam < 0 && !(await negatifKayitOnayi('satış'))) return;

    try {
      if (hemenOdendi && !seciliKasa) {
        message.error('Hemen tahsil edildi seçili ise kasa seçimi zorunludur');
        return;
      }

      const payload = {
        cari_id: seciliMusteri?.cari_id || null,
        gelir_tipi_id: seciliGelirTipi,
        fatura_durumu_id: faturaDurumu,
        aciklama: aciklama || null,
        kalemler,
        hemen_odendi: hemenOdendi,
        kasa_id: hemenOdendi ? seciliKasa : null,
        tarih: (satisTarih || dayjs()).format('YYYY-MM-DD'),
      };
      let res;
      if (editId) {
        res = await satislarAPI.update(editId, payload);
        message.success('Satış güncellendi');
      } else {
        res = await satislarAPI.create(payload);
        message.success(`Gelir faturası eklendi. Toplam: ${formatPara(res.data.toplam_tutar)}`);
      }
      setModalOpen(false);
      setEditId(null);
      resetForm();
      fetchData();
    } catch (err) {
      message.error('Kaydedilemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(s => {
      // Metin arama
      if (aramaMetni) {
        const q = aramaMetni.toLowerCase();
        if (!(
          (s.cari || '').toLowerCase().includes(q) ||
          (s.gelir_tipi || '').toLowerCase().includes(q) ||
          (s.aciklama || '').toLowerCase().includes(q)
        )) return false;
      }
      // Cari çoklu seçim
      if (!secimeUyuyor(filtreCariIds, s.cari_id)) return false;
      // Gelir tipi çoklu seçim
      if (!secimeUyuyor(filtreGelirTipiIds, s.gelir_tipi_id)) return false;
      // Fatura durumu çoklu seçim
      if (filtreFaturaDurumlari.length > 0 && !filtreFaturaDurumlari.includes(s.fatura_durumu_id)) return false;
      // Ödeme durumu çoklu seçim
      if (filtreOdenmeDurumlari.length > 0 && !filtreOdenmeDurumlari.includes(s.odendi)) return false;
      // Tutar filtresi
      if (filtreTutarOp && filtreTutarDeger != null) {
        const tutar = Number(s.toplam_tutar || 0);
        const deger = Number(filtreTutarDeger);
        if (filtreTutarOp === 'gt' && !(tutar > deger)) return false;
        if (filtreTutarOp === 'gte' && !(tutar >= deger)) return false;
        if (filtreTutarOp === 'lt' && !(tutar < deger)) return false;
        if (filtreTutarOp === 'lte' && !(tutar <= deger)) return false;
        if (filtreTutarOp === 'eq' && !(tutar === deger)) return false;
      }
      // Tarih aralığı
      if (filtreTarihBaslangic) {
        if (dayjs(s.tarih).isBefore(filtreTarihBaslangic, 'day')) return false;
      }
      if (filtreTarihBitis) {
        if (dayjs(s.tarih).isAfter(filtreTarihBitis, 'day')) return false;
      }
      return true;
    });
  }, [data, aramaMetni, filtreCariIds, filtreGelirTipiIds, filtreFaturaDurumlari, filtreOdenmeDurumlari, filtreTutarOp, filtreTutarDeger, filtreTarihBaslangic, filtreTarihBitis]);

  // Filtrelenmiş kayıtların toplamları
  const toplamlar = useMemo(() => {
    const t = { adet: filteredData.length, tutar: 0, odenen: 0, kalan: 0, odenmis: 0, odenmemis: 0, kismi: 0 };
    filteredData.forEach(s => {
      const tutar = Number(s.toplam_tutar || 0);
      const odenen = Number(s.odenen_tutar || 0);
      t.tutar += tutar;
      t.odenen += odenen;
      t.kalan += tutar - odenen;
      if (s.odendi === 1 || s.odendi === 3) t.odenmis += tutar;
      else if (s.odendi === 2) t.kismi += tutar;
      else t.odenmemis += tutar;
    });
    return t;
  }, [filteredData]);

  const filtreAktifMi = filtreCariIds.length > 0 || filtreGelirTipiIds.length > 0 || filtreFaturaDurumlari.length > 0 || filtreOdenmeDurumlari.length > 0 || filtreTutarOp || filtreTarihBaslangic || filtreTarihBitis;

  const filtreTemizle = () => {
    setFiltreCariIds([]);
    setFiltreGelirTipiIds([]);
    setFiltreFaturaDurumlari([]);
    setFiltreOdenmeDurumlari([]);
    setFiltreTutarOp(null);
    setFiltreTutarDeger(null);
    setFiltreTarihBaslangic(null);
    setFiltreTarihBitis(null);
  };

  // Cari listesini satışlardan çıkar (benzersiz)
  const cariOptions = useMemo(() => {
    const map = new Map();
    data.forEach(s => { if (s.cari_id && s.cari) map.set(s.cari_id, s.cari); });
    const list = Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    return bosSecenekliOptions(list, data, 'cari_id');
  }, [data]);

  const gelirTipiFilterOptions = useMemo(
    () => bosSecenekliOptions(gelirTipleri.map(g => ({ value: g.id, label: g.ad })), data, 'gelir_tipi_id'),
    [gelirTipleri, data]
  );

  const columns = [
    { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: isMobile ? 65 : 140, sorter: (a, b) => { const d = new Date(a.tarih || 0) - new Date(b.tarih || 0); return d !== 0 ? d : (a.id || 0) - (b.id || 0); }, defaultSortOrder: 'descend',
      render: (v) => v ? (isMobile ? dayjs(v).format('DD.MM.YY') : new Date(v).toLocaleDateString('tr-TR')) : '-' },
    { title: 'Müşteri', dataIndex: 'cari', key: 'cari', render: (v, r) => r.cari_id ? <a onClick={(e) => { e.stopPropagation(); navigate(`/cariler?detay=${r.cari_id}`); }}>{v || 'Carisiz'}</a> : (v || 'Carisiz') },
    { title: 'Gelir Tipi', dataIndex: 'gelir_tipi', key: 'gelir_tipi', responsive: ['md'] },
    { title: 'Toplam', dataIndex: 'toplam_tutar', key: 'toplam_tutar', width: isMobile ? 90 : 120, align: 'right',
      sorter: (a, b) => Number(a.toplam_tutar || 0) - Number(b.toplam_tutar || 0),
      render: (v) => v != null ? formatPara(v) : '-' },
    { title: 'Fatura Durumu', dataIndex: 'fatura_durumu_id', key: 'fatura_durumu_id', width: 140, responsive: ['md'],
      render: (id, record) => {
        const ad = record.fatura_durumu_ad || (faturaDurumlari.find(f => f.id === id)?.ad) || 'Faturasız';
        return <Tag color={FATURA_DURUMU_RENK[id] || 'default'}>{ad}</Tag>;
      },
    },
    { title: 'Ödeme', key: 'odendi', width: 140, align: 'center', responsive: ['sm'],
      render: (_, record) => {
        if (record.odendi === 3) return <Tag color="green" icon={<ThunderboltOutlined />}>Anlık Ödendi</Tag>;
        if (record.odendi === 1) return <Tag color="green" icon={<CheckCircleOutlined />}>Ödendi</Tag>;
        if (record.odendi === 2) return <Tag color="orange">Kısmi: {formatPara(record.odenen_tutar)}</Tag>;
        return <Tag color="red">Ödenmedi</Tag>;
      },
    },
    { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama', responsive: ['lg'] },
    {
      title: 'İşlem', key: 'islem', width: isMobile ? 120 : 210,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleVadeAc(record)} title="Vade & Eşleşme" />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleDuzenle(record.id)}>
            {!isMobile && 'Düzenle'}
          </Button>
          <Popconfirm title="Bu satışı silmek istediğinize emin misiniz? İlgili tahsilatlar ve stok hareketleri geri alınacak."
            onConfirm={() => handleSil(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />}>
              {!isMobile && 'Sil'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleVadeAc = async (record) => {
    setVadeData({ kayit: record, vadeler: [] });
    setVadeModalOpen(true);
    try {
      const res = await satisVadeleriAPI.getBySatis(record.id);
      setVadeData({ kayit: record, vadeler: res.data || [] });
    } catch (err) {
      message.error('Vadeler yüklenemedi');
    }
  };

  // O güne ait satış takviminde tanımlı ürünler + (varsa) o günün menüsündeki ürünler
  // + ekstra menülerdeki ürünler. Düzenleme sırasında mevcut kalemler de korunur.
  const izinliUrunIds = useMemo(() => {
    const ids = new Set();
    gununTakvimUrunleri.forEach(t => {
      if (t.takvim_tipi === 'urun' && t.ilgili_id) ids.add(t.ilgili_id);
    });
    (gununMenusu?.kalemler || []).forEach(k => {
      if (k.urun_id) ids.add(k.urun_id);
    });
    gununEkstraMenuleri.forEach(em => {
      (em.kalemler || []).forEach(k => { if (k.urun_id) ids.add(k.urun_id); });
    });
    ilaveler.forEach(i => {
      if (i.urun_id && typeof i.urun_id === 'number') ids.add(i.urun_id);
    });
    return ids;
  }, [gununTakvimUrunleri, gununMenusu, gununEkstraMenuleri, ilaveler]);

  const ilaveMenuKayitliMi = ilaveler.some(i => i.urun_id === 'menu');
  const urunOptions = [
    ...((gununMenusu || ilaveMenuKayitliMi) ? [{ value: 'menu', label: 'Günün Menüsü' }] : []),
    ...gununEkstraMenuleri.map(em => ({ value: `ekstra_${em.id}`, label: `Menü: ${em.ad}` })),
    ...urunler.filter(u => izinliUrunIds.has(u.id)).map(u => ({ value: u.id, label: u.urun_adi })),
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Satışlar</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          {selectedRowKeys.length > 0 && (
            <Popconfirm title={`${selectedRowKeys.length} satış silinecek. Devam edilsin mi? İlgili tahsilatlar ve stok hareketleri geri alınacak.`}
              onConfirm={handleTopluSil} okText="Evet" cancelText="Hayır">
              <Button danger icon={<DeleteOutlined />} title="Seçilenleri Sil">
                {isMobile ? `(${selectedRowKeys.length})` : `Seçilenleri Sil (${selectedRowKeys.length})`}
              </Button>
            </Popconfirm>
          )}
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
          <Button icon={<FilterOutlined />} onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])}
            type={filtreAktifMi ? 'primary' : 'default'}
            ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}>
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleModalAc} title="Yeni Satış">
            {!isMobile && 'Yeni Satış'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost
        items={[{
          key: '1',
          showArrow: false,
          header: null,
          children: (
            <Card size="small" style={{ marginBottom: 16 }}
              title={<span><FilterOutlined /> Akıllı Filtreler</span>}
              extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
              <Row gutter={[16, 12]}>
                <Col xs={24} sm={12} md={8}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Cari / Müşteri</label>
                  <Select mode="multiple" style={{ width: '100%' }} placeholder="Cari seçin..."
                    options={cariOptions} value={filtreCariIds} onChange={setFiltreCariIds}
                    showSearch optionFilterProp="label" maxTagCount={2} allowClear />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Gelir Tipi</label>
                  <Select mode="multiple" style={{ width: '100%' }} placeholder="Gelir tipi seçin..."
                    options={gelirTipiFilterOptions}
                    value={filtreGelirTipiIds} onChange={setFiltreGelirTipiIds}
                    maxTagCount={2} allowClear />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Fatura Durumu</label>
                  <Select mode="multiple" style={{ width: '100%' }} placeholder="Fatura durumu seçin..."
                    options={faturaDurumuOptions}
                    value={filtreFaturaDurumlari} onChange={setFiltreFaturaDurumlari}
                    maxTagCount={2} allowClear />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Ödeme Durumu</label>
                  <Select mode="multiple" style={{ width: '100%' }} placeholder="Ödeme durumu seçin..."
                    options={[
                      { value: 0, label: 'Ödenmedi' },
                      { value: 1, label: 'Ödendi' },
                      { value: 2, label: 'Kısmi Ödendi' },
                      { value: 3, label: 'Anlık Ödendi' },
                    ]}
                    value={filtreOdenmeDurumlari} onChange={setFiltreOdenmeDurumlari}
                    maxTagCount={2} allowClear />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tutar Filtresi</label>
                  <Space.Compact style={{ width: '100%' }}>
                    <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreTutarOp} onChange={setFiltreTutarOp} allowClear
                      options={[
                        { value: 'gt', label: 'Büyüktür (>)' },
                        { value: 'gte', label: 'Büyük Eşit (>=)' },
                        { value: 'lt', label: 'Küçüktür (<)' },
                        { value: 'lte', label: 'Küçük Eşit (<=)' },
                        { value: 'eq', label: 'Eşittir (=)' },
                      ]} />
                    <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Tutar" value={filtreTutarDeger}
                      onChange={setFiltreTutarDeger} min={0} />
                  </Space.Compact>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Başlangıç</label>
                  <DatePicker style={{ width: '100%' }} placeholder="Başlangıç tarihi"
                    value={filtreTarihBaslangic} onChange={setFiltreTarihBaslangic}
                    format="DD.MM.YYYY" />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Bitiş</label>
                  <DatePicker style={{ width: '100%' }} placeholder="Bitiş tarihi"
                    value={filtreTarihBitis} onChange={setFiltreTarihBitis}
                    format="DD.MM.YYYY" />
                </Col>
              </Row>
            </Card>
          ),
        }]}
      />

      {/* Toplam bilgiler */}
      {(
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Kayıt Sayısı" value={toplamlar.adet} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Toplam Tutar" value={toplamlar.tutar} precision={2} suffix="₺" />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Ödenen" value={toplamlar.odenen} precision={2} suffix="₺" valueStyle={{ color: '#3f8600' }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Kalan" value={toplamlar.kalan} precision={2} suffix="₺" valueStyle={{ color: toplamlar.kalan > 0 ? '#cf1322' : '#3f8600' }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Ödenmemiş" value={toplamlar.odenmemis} precision={2} suffix="₺" valueStyle={{ color: '#cf1322' }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Kısmi Ödenen" value={toplamlar.kismi} precision={2} suffix="₺" valueStyle={{ color: '#d48806' }} />
            </Col>
          </Row>
        </Card>
      )}

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
        pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      <Modal
        title={editId ? 'Satış Düzenle' : 'Yeni Satış'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditId(null); resetForm(); }}
        onOk={handleKaydet}
        width={800}
        okText={editId ? 'Güncelle' : 'Kaydet'}
        okButtonProps={{ disabled: !seciliGelirTipi }}
      >
        {/* 1. Gelir Tipi Seçimi + Tarih */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 600 }}>Gelir Tipi *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#666' }}>Tarih:</span>
              <DatePicker
                value={satisTarih}
                onChange={handleTarihChange}
                format="DD.MM.YYYY"
                allowClear={false}
              />
            </div>
          </div>
          <Space wrap>
            {gelirTipleri.map(g => (
              <Button
                key={g.id}
                type={seciliGelirTipi === g.id ? 'primary' : 'default'}
                onClick={() => handleGelirTipiSec(g.id)}
                size="large"
              >
                {g.ad}
              </Button>
            ))}
          </Space>
        </div>

        {/* 2. Gelir tipi seçildiyse müşteri seçimi (sabit için zorunlu, diğerlerinde opsiyonel) */}
        {seciliGelirTipi && (
          <>
            <Divider orientation="left">Müşteri Bilgileri</Divider>
            <div style={{ display: 'grid', gridTemplateColumns: isSabit ? '3fr 1fr' : '1fr', gap: '0 16px', marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                  {isSabit ? 'Müşteri *' : 'Müşteri (Opsiyonel)'}
                </label>
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  allowClear={!isSabit}
                  optionFilterProp="label"
                  placeholder={isSabit ? 'Müşteri seçin' : 'Müşteri seçin (opsiyonel)'}
                  options={musteriler.map(m => ({ value: m.cari_id, label: m.ad }))}
                  onChange={handleMusteriSec}
                  value={seciliMusteri?.cari_id}
                />
              </div>
              {isSabit && (
                <div>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Günün Menüsü Adet</label>
                  <InputNumber style={{ width: '100%' }} min={1} value={menuAdet} onChange={setMenuAdet} />
                </div>
              )}
            </div>

            {isSabit && seciliMusteri && (
              <div style={{
                background: anlasmaBirimFiyat > 0 ? '#f6ffed' : '#fff7e6',
                border: `1px solid ${anlasmaBirimFiyat > 0 ? '#b7eb8f' : '#ffd591'}`,
                borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong>{seciliMusteri.ad}</strong>
                  {sabitFiyatTipi === 'sabit' ? (
                    <>{' '}— Anlaşma: <strong>{formatPara(sabitBirimFiyat)}</strong> (KDV hariç)</>
                  ) : (
                    <>
                      {' '}— <Tag color={sabitFiyatTipi === 'yuzde_indirim' ? 'purple' : 'orange'}>
                        {sabitFiyatTipi === 'yuzde_indirim' ? `%${Number(sabitIndirimDegeri)} indirim` : `${formatPara(sabitIndirimDegeri)} indirim`}
                      </Tag>
                      Günün menüsü: <strong>{formatPara(sabitMenuTabanDahil)}</strong>
                      {' → '}İndirimli: <strong>{formatPara(sabitMenuIndirimliDahil)}</strong> (KDV dahil)
                    </>
                  )}
                  {' '}×{menuAdet} = <strong>{formatPara(menuToplamKdvHaric)}</strong> (KDV hariç)
                  {' '}KDV:
                  <Select size="small" style={{ width: 70 }} value={menuKdvOrani} onChange={setMenuKdvOrani} options={kdvOranlari} />
                  {formatPara(menuKdv)}
                  {' | '}Fatura: <Tag color={seciliMusteri.faturali ? 'orange' : 'default'}>{seciliMusteri.faturali ? 'Beklemede' : 'Faturasız'}</Tag>
                </div>
                {anlasmaBirimFiyat === 0 && (
                  <div style={{ marginTop: 4, color: '#cf1322' }}>
                    {sabitFiyatTipi === 'sabit'
                      ? 'Bu tarih için anlaşma fiyatı bulunamadı. Müşteri sayfasından tarihçeye satır ekleyin.'
                      : 'Bu tarih için günün menüsü tanımlı değil ya da indirimli fiyat 0 çıkıyor. İndirim tabanı için önce günün menüsünü tanımlayın.'}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* 3. Sabit dışı veya İlave ürünler */}
        {seciliGelirTipi && (
          <>
            <Divider orientation="left">{isSabit ? 'İlave Ürünler (Opsiyonel)' : 'Ürünler / Menüler'}</Divider>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
              {isSabit ? 'Ek ürün veya menü eklemek isterseniz liste fiyatı (KDV dahil) uygulanır.' : 'Liste fiyatı (KDV dahil) uygulanır.'}
            </div>

            {ilaveler.map((item, idx) => (
              <div key={item.key} style={{ background: '#fafafa', borderRadius: 8, padding: 10, marginBottom: 6, border: '1px solid #f0f0f0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 70px 100px 100px 90px 36px', gap: '0 8px', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#999' }}>Ürün</label>
                    <Select size="small" style={{ width: '100%' }}
                      value={item.urun_id}
                      onChange={(v) => ilaveGuncelle(item.key, 'urun_id', v)}
                      options={urunOptions}
                      placeholder="Seçin" showSearch optionFilterProp="label" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#999' }}>Adet</label>
                    <InputNumber size="small" style={{ width: '100%' }} min={1} value={item.adet}
                      onChange={(v) => ilaveGuncelle(item.key, 'adet', v)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#999' }}>KDV</label>
                    <Select size="small" style={{ width: '100%' }} value={item.kdv_orani}
                      onChange={(v) => ilaveGuncelle(item.key, 'kdv_orani', v)}
                      options={kdvOranlari} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#999' }}>Fiyat (KDV hariç)</label>
                    <InputNumber {...paraInputProps} size="small" style={{ width: '100%' }}
                      value={item.fiyatKdvHaric}
                      onChange={(v) => ilaveGuncelle(item.key, 'fiyatKdvHaric', v)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#999' }}>Fiyat (KDV dahil)</label>
                    <InputNumber {...paraInputProps} size="small" style={{ width: '100%' }}
                      value={item.fiyatKdvDahil}
                      onChange={(v) => ilaveGuncelle(item.key, 'fiyatKdvDahil', v)} />
                  </div>
                  <div style={{ textAlign: 'right', paddingBottom: 2 }}>
                    <div style={{ fontSize: 11, color: '#999' }}>Toplam</div>
                    <strong>{formatPara((item.fiyatKdvDahil || 0) * (item.adet || 0))}</strong>
                  </div>
                  <div style={{ paddingBottom: 2 }}>
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => ilaveSil(item.key)} />
                  </div>
                </div>
              </div>
            ))}

            <Button type="dashed" icon={<PlusOutlined />} onClick={ilaveEkle} style={{ marginTop: 4 }}>
              {isSabit ? 'İlave Ekle' : 'Ürün Ekle'}
            </Button>
          </>
        )}

        {/* 4. Ödeme + Fatura Durumu + Açıklama */}
        {seciliGelirTipi && (
          <>
            <Divider orientation="left">Ödeme & Fatura</Divider>

            <div style={{
              background: hemenOdendi ? '#f6ffed' : '#fafafa',
              border: `1px solid ${hemenOdendi ? '#b7eb8f' : '#f0f0f0'}`,
              borderRadius: 8, padding: 12, marginBottom: 16
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: hemenOdendi ? 12 : 0 }}>
                <Switch checked={hemenOdendi} onChange={setHemenOdendi} />
                <span style={{ fontWeight: 600 }}>Hemen tahsil edildi</span>
                {hemenOdendi && <span style={{ fontSize: 12, color: '#52c41a' }}>Tahsilat otomatik oluşturulacak ve kasa bakiyesi güncellenecek</span>}
              </div>
              {hemenOdendi && (
                <div>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Kasa *</label>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                    placeholder="Ödemenin gireceği kasayı seçin"
                    options={kasalar.map(k => ({ value: k.id, label: k.ad }))}
                    value={seciliKasa}
                    onChange={(v) => {
                      setSeciliKasa(v);
                      const kasa = kasalar.find(k => k.id === v);
                      if (kasa && kasa.ad.toLowerCase().replace(/\s/g, '').startsWith('kuveyttürk')) {
                        setFaturaDurumu(FATURA_DURUMU.FIS_KESILDI);
                      }
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 16px', marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Fatura Durumu</label>
                <Select style={{ width: '100%' }} value={faturaDurumu} onChange={setFaturaDurumu}
                  options={faturaDurumuOptions} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Açıklama</label>
                <Input value={aciklama} onChange={(e) => setAciklama(e.target.value)} />
              </div>
            </div>

            {/* Özet */}
            <Card size="small" style={{ background: '#FAFAFA' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>Ara Toplam (KDV hariç):</span>
                <span>{formatPara(genelKdvHaric)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>KDV:</span>
                <span>{formatPara(genelKdv)}</span>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16 }}>
                <span>KDV Dahil Toplam:</span>
                <span style={{ color: '#FF6B00' }}>{formatPara(genelToplam)}</span>
              </div>
            </Card>
          </>
        )}
      </Modal>

      <Modal
        title={vadeData.kayit ? `Satış #${vadeData.kayit.id} — Vade & Eşleşme Detayı` : 'Vade Detayı'}
        open={vadeModalOpen}
        onCancel={() => setVadeModalOpen(false)}
        footer={<Button onClick={() => setVadeModalOpen(false)}>Kapat</Button>}
        width={isMobile ? '95vw' : 800}
      >
        {vadeData.vadeler.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
            Vade kaydı yok (satış tutarı 0 veya henüz yüklenmedi)
          </div>
        ) : (
          vadeData.vadeler.map(vade => (
            <Card key={vade.id} size="small" style={{ marginBottom: 12 }}
              title={`Taksit ${vade.taksit_no} — Vade: ${dayjs(vade.vade_tarihi).format('DD.MM.YYYY')}`}
              extra={
                <Tag color={vade.durum === 'kapali' ? 'green' : vade.durum === 'kismi' ? 'orange' : 'red'}>
                  {vade.durum.toUpperCase()}
                </Tag>
              }
            >
              <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={8}><Statistic title="Tutar" value={vade.tutar} precision={2} suffix="₺" /></Col>
                <Col span={8}><Statistic title="Ödenen" value={Number(vade.tutar) - Number(vade.kalan_tutar)} precision={2} suffix="₺" valueStyle={{ color: '#3f8600' }} /></Col>
                <Col span={8}><Statistic title="Kalan" value={vade.kalan_tutar} precision={2} suffix="₺" valueStyle={{ color: '#cf1322' }} /></Col>
              </Row>
              {vade.eslesmeler.length > 0 && (
                <Table
                  size="small"
                  dataSource={vade.eslesmeler}
                  rowKey="id"
                  pagination={false}
                  columns={[
                    { title: 'Eşleşme Tarihi', dataIndex: 'eslesme_tarihi', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-' },
                    { title: 'Tahsilat ID', dataIndex: 'kaynak_id', render: (v) => <a onClick={() => navigate(`/tahsilatlar?duzenle=${v}`)}>#{v}</a> },
                    { title: 'İşlem Tarihi', dataIndex: 'islem_tarihi', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-' },
                    { title: 'Kasa', dataIndex: 'kasa_ad' },
                    { title: 'Tutar', dataIndex: 'tutar', align: 'right', render: (v) => <strong>{formatPara(v)}</strong> },
                  ]}
                />
              )}
            </Card>
          ))
        )}
      </Modal>
    </div>
  );
}

export default Satislar;
